import classNames from 'classnames';
import React, {useContext, useEffect, useState} from 'react';
import FilterTags from 'src/components/college-search/filter-tags';
import FilteredColleges from 'src/components/college-search/filtered-colleges';
import Filters from 'src/components/college-search/filters';
import FiltersMobile from 'src/components/college-search/filters-mobile';
import Header from 'src/components/college-search/header';
import LoadingSearch from 'src/components/college-search/loading-search';
import SortByMobile from 'src/components/college-search/sort-by-modal-mobile';
import StatusNotification from 'src/components/college-search/status-notification';
import TermNotOfferedModal from 'src/components/college-search/term-not-offered-modal';
import {PlatformContext} from 'src/components/platform-context';
import {UserType} from 'src/types/enums';
import useUserType from 'src/hooks/use-user-type/use-user-type';
import InlineCollegeSearchBar from './inline-college-search-bar';
import {useMediaQuery} from 'src/hooks/use-media-query';
import type {
  ApplicationStatusObject,
  CollegeSearch as CollegeSearchType,
  ObjectOf,
} from 'src/types/store';
import useFeatureFlags from 'src/hooks/use-feature-flags';

interface Props {
  collegeSearch: CollegeSearchType;
  getColleges: () => void;
  followCollege: (collegeId: string) => void;
  unfollowCollege: (collegeId: string) => void;
  resetPage: () => void;
  getCollegeAppStatuses: () => void;
  setCollegeAppStatus: (collegeId: string, status: string) => void;
  collegeApplicationStatuses: ObjectOf<ApplicationStatusObject>;
  setNavigationAnnouncement: (announcement: string) => void;
  openTermNotOfferedModal: () => void;
  initializeCollegeSearch: (userType: string) => void;
  filtersInitialized: boolean;
}

const CollegeSearch = ({
  collegeSearch,
  getColleges,
  followCollege,
  unfollowCollege,
  resetPage,
  getCollegeAppStatuses,
  setCollegeAppStatus,
  collegeApplicationStatuses,
  setNavigationAnnouncement,
  openTermNotOfferedModal,
  initializeCollegeSearch,
  filtersInitialized,
}: Props) => {
  const {userType, isTransfer, isHighSchool} = useUserType();
  const {isIonic} = useContext(PlatformContext);

  const [state, setState] = useState({
    initializedColleges: false,
  });

  const isHsOrTransferStudent = isHighSchool || isTransfer;
  const mq = useMediaQuery();
  const {collegeApplicationStatusFlag} = useFeatureFlags(['college_application_status']);

  useEffect(() => {
    resetPage();

    setNavigationAnnouncement('Colleges');

    if (userType !== UserType.HS_STUDENT) {
      initializeCollegeSearch(userType);
    }
  }, []);

  useEffect(() => {
    if (isHsOrTransferStudent && collegeApplicationStatusFlag) {
      getCollegeAppStatuses();
    }
  }, [isHsOrTransferStudent, collegeApplicationStatusFlag, getCollegeAppStatuses]);

  useEffect(() => {
    if (filtersInitialized && !state.initializedColleges) {
      setState((prev) => ({...prev, initializedColleges: true}));
      getColleges();
    }
  }, [filtersInitialized, state.initializedColleges, getColleges]);

  const {loading} = collegeSearch;

  return (
    <div
      className={classNames('college-search-container', {
        '--no-tabs': userType === UserType.EDUCATOR || (mq.MS_AND_DOWN && isTransfer),
        '--ionic': isIonic,
      })}
    >
      <InlineCollegeSearchBar />
      <Header />
      <div
        className="college-search"
        aria-label={`This page includes a list of colleges that may add more colleges to
          the list when the user scrolls to the bottom of the page. If automatic
          loading does not occur, a "Load more colleges" button may appear at
          the bottom of the list to fetch more colleges. There is no other
          content below the list of colleges.`}
      >
        {loading ? (
          <LoadingSearch />
        ) : (
          <>
            <div className="college-search-sidebar-desktop">
              <Filters />
            </div>
            <div className="college-search-body">
              <div className="college-search-filter-tags">
                <FilterTags />
              </div>
              {isTransfer && <TermNotOfferedModal />}
              <FilteredColleges
                collegeSearch={collegeSearch}
                followCollege={followCollege}
                unfollowCollege={unfollowCollege}
                getColleges={getColleges}
                setCollegeAppStatus={setCollegeAppStatus}
                collegeApplicationStatuses={collegeApplicationStatuses}
                openTermNotOfferedModal={openTermNotOfferedModal}
                showApplicationStatusMenu={collegeApplicationStatusFlag}
              />
            </div>
            <FiltersMobile />
            <SortByMobile />
            {isHighSchool && <StatusNotification />}
          </>
        )}
      </div>
    </div>
  );
};

export default CollegeSearch;
